import { type EntitlementSet, PLAN_DEFAULTS, type Plan } from '@parcel/types';
import { useQuery } from '@tanstack/react-query';
import { isSupabaseConfigured } from './supabase.js';
import { getWorkspace } from './workspaces.js';

export interface EntitlementsView {
  effectivePlan: Plan;
  entitlements: EntitlementSet;
  isLoading: boolean;
}

/**
 * Resolve the client entitlements view, failing CLOSED to Free until the
 * workspace bootstrap resolves. The server is the security boundary; this is
 * display convenience only.
 */
export function resolveEntitlementsView(
  data: { effectivePlan?: Plan; entitlements?: EntitlementSet } | undefined,
  isLoading: boolean,
): EntitlementsView {
  return {
    effectivePlan: data?.effectivePlan ?? 'free',
    entitlements: data?.entitlements ?? PLAN_DEFAULTS.free,
    isLoading,
  };
}

/**
 * Reads plan + entitlements off the existing workspace bootstrap query
 * (`['workspace', slug]`), so it is a cache hit on any page that already
 * loaded the workspace - no extra request.
 */
export function useEntitlements(workspaceSlug: string | null | undefined): EntitlementsView {
  const query = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
    // A gate that mounts after a failed load reads Free; refetching on mount loops while a parent remounts it.
    retryOnMount: false,
  });
  return resolveEntitlementsView(query.data, query.isLoading);
}
